import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

/**
 * useSortableData — small hook for client-side table sorting.
 *
 * Returns { items, sortBy, sortDir, requestSort } where
 *   requestSort(key) toggles asc → desc → null (cycle) and re-sorts items.
 *
 * Accepts a key function map { [columnKey]: (row) => value } for custom accessors.
 */
export function useSortableData(rows, { initialKey = null, initialDir = "asc", accessors = {} } = {}) {
  const [sortBy, setSortBy] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const items = useMemo(() => {
    if (!sortBy) return rows;
    const fn = accessors[sortBy] || ((r) => r?.[sortBy]);
    const sorted = [...rows].sort((a, b) => {
      const va = fn(a);
      const vb = fn(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortBy, sortDir]);

  const requestSort = (key) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") setSortDir("desc");
    else { setSortBy(null); setSortDir("asc"); }
  };

  return { items, sortBy, sortDir, requestSort };
}

/**
 * SortableTH — drop-in <th> with a sort-toggle indicator.
 *   <SortableTH columnKey="code" active={sortBy} dir={sortDir} onClick={requestSort}>Code</SortableTH>
 */
export function SortableTH({ columnKey, active, dir, onClick, children, className = "", align = "left" }) {
  const isActive = active === columnKey;
  return (
    <th
      onClick={() => onClick?.(columnKey)}
      data-testid={`sort-th-${columnKey}`}
      className={`px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold select-none cursor-pointer hover:bg-slate-100 ${align === "right" ? "text-right" : "text-left"} ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive
          ? (dir === "desc" ? <ChevronDown className="h-3 w-3 text-[#226DB4]" /> : <ChevronUp className="h-3 w-3 text-[#226DB4]" />)
          : <ChevronsUpDown className="h-3 w-3 text-slate-300" />}
      </span>
    </th>
  );
}

/**
 * EmptyState — standardized empty-state cell content.
 */
export function EmptyState({ message = "No data to display", colSpan = 1 }) {
  return (
    <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400 italic">{message}</td></tr>
  );
}

/**
 * LoadingRow — placeholder skeleton row.
 */
export function LoadingRow({ colSpan = 1 }) {
  return (
    <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
  );
}
